class ProductPriceCorrector extends HTMLElement {
  constructor () {
    super()
    this.style.display = 'contents'
  }

  connectedCallback () {
    this.observer = new MutationObserver(() => {
      ProductPriceCorrector.correctPricesIn(this)
    })

    this.observer.observe(this, {
      childList: true,
      subtree: true
    })

    ProductPriceCorrector.correctPricesIn(this)
  }

  disconnectedCallback () {
    this.observer?.disconnect()
  }

  static API_CONFIG = {
    endpoint: `https://${window.Shopify.shop}/api/2025-07/graphql.json`,
    accessToken:
      window.Shopify.shop === 'holland-cooper-us.myshopify.com'
        ? '4b30dc1c20ff119f17b4c3a75a433ad0'
        : 'b03aa3be758de5476316ebef2ea64729'
  }

  static pendingElements = new WeakSet()
  static pendingHandleRequests = new Map()
  static priceCache = new Map()

  static PRICE_SCOPE = 'product-price-corrector, .nosto_element, [data-module="nosto-product-grid"]'

  static country () {
    const country = window.Shopify?.country ? window.Shopify.country.toUpperCase() : ''

    return /^[A-Z]{2}$/.test(country) ? country : null
  }

  static locale () {
    return window.Shopify?.locale || document.documentElement.lang || 'en-GB'
  }

  static isNostoPrice (priceElement) {
    return Boolean(priceElement.closest(this.PRICE_SCOPE))
  }

  static handleFromUrl (href) {
    if (!href) return null

    const match = href.split('?')[0].match(/\/products\/([^/]+)/)
    return match ? decodeURIComponent(match[1]) : null
  }

  static getProductHandle (priceElement) {
    const attributedHandle = priceElement.getAttribute('data-product-handle')
    if (attributedHandle) return attributedHandle

    const card = priceElement.closest(
      '[data-product-card-handle], [data-product-handle], .prd-Card, .product-card'
    )

    if (!card) return this.handleFromUrl(priceElement.closest('a[href*="/products/"]')?.href)

    return (
      card.getAttribute('data-product-card-handle') ||
      card.getAttribute('data-product-handle') ||
      card.querySelector('[data-product-handle]')?.getAttribute('data-product-handle') ||
      this.handleFromUrl(card.querySelector('a[href*="/products/"]')?.getAttribute('href'))
    )
  }

  static correctPricesIn (root) {
    root.querySelectorAll('.prd-Price_Current').forEach(priceElement => {
      this.correctPriceElement(priceElement)
    })
  }

  static async correctPriceElement (priceElement) {
    if (!this.isNostoPrice(priceElement)) return

    const handle = this.getProductHandle(priceElement)
    if (!handle) return

    if (priceElement.dataset.priceCorrectedHandle === handle) return
    if (this.pendingElements.has(priceElement)) return

    this.pendingElements.add(priceElement)

    try {
      const result = await this.getProductPrice(handle)
      if (!result) return

      this.applyPrice(priceElement, handle, result)
    } catch (error) {
      console.error(`Failed to update price for ${handle}:`, error)
    } finally {
      this.pendingElements.delete(priceElement)
    }
  }

  static applyPrice (priceElement, handle, result) {
    priceElement.textContent = result.price
    priceElement.dataset.priceCorrectedHandle = handle

    const priceSaleElement = priceElement
      .closest('.prd-Price, .prd-Card_PriceContainer, .prd-Card_Price')
      ?.querySelector('.prd-Price_Old')

    if (priceSaleElement) {
      if (result.salePrice) {
        priceSaleElement.textContent = result.salePrice
        priceSaleElement.hidden = false
      } else {
        priceSaleElement.textContent = ''
        priceSaleElement.hidden = true
      }
    }
  }

  static async getProductPrice (handle) {
    if (this.priceCache.has(handle)) {
      return this.priceCache.get(handle)
    }

    if (this.pendingHandleRequests.has(handle)) {
      return this.pendingHandleRequests.get(handle)
    }

    const request = this.fetchProductPrice(handle)
      .then(result => {
        if (result) {
          this.priceCache.set(handle, result)
        }
        return result
      })
      .finally(() => {
        this.pendingHandleRequests.delete(handle)
      })

    this.pendingHandleRequests.set(handle, request)
    return request
  }

  static async fetchProductPrice (handle) {
    const country = this.country()
    const contextDirective = country ? `@inContext(country: ${country})` : ''

    const query = `
      query getProduct($handle: String!) ${contextDirective} {
        product(handle: $handle) {
          variants(first: 1) {
            edges {
              node {
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    `

    const response = await fetch(this.API_CONFIG.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.API_CONFIG.accessToken
      },
      body: JSON.stringify({
        query,
        variables: { handle }
      })
    })

    const result = await response.json()

    if (result.errors) {
      console.error('GraphQL errors:', result.errors)
      return null
    }

    const variant = result.data?.product?.variants?.edges?.[0]?.node
    if (!variant) return null

    const price = variant.price
    const compareAt = variant.compareAtPrice
    const formattedPrice = this.formatPrice(price.amount, price.currencyCode)

    if (!compareAt || parseFloat(compareAt.amount) <= parseFloat(price.amount)) {
      return { price: formattedPrice, salePrice: null }
    }

    return {
      price: formattedPrice,
      salePrice: this.formatPrice(compareAt.amount, compareAt.currencyCode)
    }
  }

  static formatPrice (amount, currencyCode) {
    try {
      return new Intl.NumberFormat(this.locale(), {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(parseFloat(amount))
    } catch (error) {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currencyCode || 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(parseFloat(amount))
    }
  }
}

if (!customElements.get('product-price-corrector')) {
  customElements.define('product-price-corrector', ProductPriceCorrector)
}

const startGlobalPriceCorrection = () => {
  ProductPriceCorrector.correctPricesIn(document)

  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType !== Node.ELEMENT_NODE) return

        if (node.matches?.('.prd-Price_Current')) {
          ProductPriceCorrector.correctPriceElement(node)
        }

        node.querySelectorAll?.('.prd-Price_Current').forEach(priceElement => {
          ProductPriceCorrector.correctPriceElement(priceElement)
        })
      })
    })
  })

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startGlobalPriceCorrection)
} else {
  startGlobalPriceCorrection()
}
